import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, InputAdornment, TextField, Typography,
} from '@mui/material';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../components/shared/ConfirmDialog';
import { alpha, useTheme } from '@mui/material/styles';
import { Search, DeleteOutline } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Breadcrumb } from '../../components/layout/Breadcrumb';
import { ROUTES } from '../../constants/routes';
import { getQuotes, createQuote, deleteQuote } from '../../services/quoteService';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../auth/useAuth';
import { QuickAddInternalQuoteDialog } from './QuickAddInternalQuoteDialog';
import { QuotesSideRail, QUOTE_FILTERS, type QuoteFilter } from './QuotesSideRail';
import type { QuoteWithClient } from '../../types/quote';

type Filter = QuoteFilter;

const STATUS_COLORS: Record<string, string> = {
  Draft: '#9e9e9e', 'Internal Review': '#e65100', 'Approved Internal': '#2e7d32', Rejected: '#d32f2f', 'Changes Requested': '#e65100',
  Sent: '#1976d2', Viewed: '#0288d1', Accepted: '#00695c',
  Declined: '#c62828', Expired: '#616161', Withdrawn: '#757575', Converted: '#00695c',
};

const fmt = (v: number | null, c: string) =>
  v != null ? v.toLocaleString('en-CA', { style: 'currency', currency: c || 'CAD' }) : '';

export const QuotesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { user } = useAuth();
  const { role } = usePermissions();
  const canDelete = role === 'technician' || role === 'admin' || role === 'superadmin';
  const [quotes, setQuotes] = useState<QuoteWithClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  // Flash highlight for recently approved/rejected quote
  useEffect(() => {
    const key = searchParams.get('highlight');
    if (key) {
      setHighlightKey(key);
      const t = setTimeout(() => setHighlightKey(null), 3000);
      return () => clearTimeout(t);
    }
  }, [searchParams]);

  // Deep-link support from the shared side rail on OTHER pages:
  // /quotes?filter=<key> selects a filter; ?new=1 / ?newInternal=1 open the
  // create flows. Params are consumed once, then stripped from the URL.
  useEffect(() => {
    const f = searchParams.get('filter');
    const isNew = searchParams.get('new') === '1';
    const isNewInternal = searchParams.get('newInternal') === '1';
    if (!f && !isNew && !isNewInternal) return;
    if (f && QUOTE_FILTERS.some((x) => x.key === f)) setFilter(f as Filter);
    if (isNew) handleNewQuote();
    if (isNewInternal) setInternalDlgOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('filter'); next.delete('new'); next.delete('newInternal');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try { setQuotes(await getQuotes()); } catch { setQuotes([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const toggleSelect = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((q) => q.QuoteKey)));
  };

  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
  const handleBulkDelete = async () => {
    // Failures must SURFACE (finalized-scenario / committed-work 409s used to
    // be swallowed — the quote silently stayed in the list).
    const keys = Array.from(selected);
    const results = await Promise.allSettled(keys.map((k) => deleteQuote(k)));
    const failures = results
      .map((r, i) => (r.status === 'rejected' ? { key: keys[i], reason: r.reason as { response?: { data?: { error?: string } }; message?: string } } : null))
      .filter(Boolean) as { key: string; reason: { response?: { data?: { error?: string } }; message?: string } }[];
    if (failures.length > 0) {
      const labelOf = (k: string) => quotes.find((q) => q.QuoteKey === k)?.QuoteNumber ?? k.slice(0, 8);
      const firstMsg = failures[0].reason.response?.data?.error || failures[0].reason.message || 'delete failed';
      setBulkDeleteError(
        `${failures.length} of ${keys.length} quote${keys.length === 1 ? '' : 's'} could not be deleted `
        + `(${failures.map((f) => labelOf(f.key)).join(', ')}): ${firstMsg} `
        + 'Open the quote and delete it from there to see the full schedule-release confirmation.',
      );
    } else {
      setBulkDeleteError(null);
    }
    setSelected(new Set());
    fetchQuotes();
  };

  const handleNewQuote = async () => {
    try {
      const q = await createQuote({});
      navigate(ROUTES.QUOTE_EDITOR.replace(':quoteKey', q.QuoteKey));
    } catch { /* */ }
  };

  // BUG-2026-0121 #1 — quick "New Internal Quote" entry (skips approval).
  const [internalDlgOpen, setInternalDlgOpen] = useState(false);
  const handleInternalCreated = (quoteKey: string) => {
    setInternalDlgOpen(false);
    navigate(ROUTES.QUOTE_EDITOR.replace(':quoteKey', quoteKey));
  };

  // Counts per status for the sidebar badges
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: quotes.length, mine: 0 };
    for (const q of quotes) {
      c[q.Status] = (c[q.Status] || 0) + 1;
      if (q.PreparedByUserId === user?.email) c.mine++;
    }
    return c;
  }, [quotes, user?.email]);

  const filtered = useMemo(() => {
    let list = quotes;
    if (filter === 'mine') list = list.filter((q) => q.PreparedByUserId === user?.email);
    else if (filter !== 'all') list = list.filter((q) => q.Status === filter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((q) =>
        (q.Title ?? '').toLowerCase().includes(s) ||
        (q.ClientName ?? '').toLowerCase().includes(s) ||
        (q.QuoteNumber ?? '').toLowerCase().includes(s),
      );
    }
    return list;
  }, [quotes, filter, search]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      <QuotesSideRail
        active={filter}
        counts={counts}
        onSelectFilter={(key) => setFilter(key)}
        onNewQuote={handleNewQuote}
        onNewInternalQuote={() => setInternalDlgOpen(true)}
      />

      {/* Main content */}
      <Box sx={{ flex: 1, p: 2.5, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Breadcrumb items={[{ label: 'Quotes' }]} />
          {canDelete && selected.size > 0 && (
            <Button variant="outlined" size="small" color="error" startIcon={<DeleteOutline />}
              onClick={() => setConfirmDlg({
                open: true, title: 'Delete Quotes',
                message: `Permanently delete ${selected.size} selected quote${selected.size > 1 ? 's' : ''}? This cannot be undone.`,
                confirmLabel: 'Delete', confirmColor: 'error', onConfirm: handleBulkDelete,
              })}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              Delete Selected ({selected.size})
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <TextField size="small" placeholder="Search quotes..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
            sx={{ width: 240, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.8rem' } }} />
        </Box>

        {bulkDeleteError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setBulkDeleteError(null)}>
            {bulkDeleteError}
          </Alert>
        )}

        {loading ? (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>Loading quotes...</Typography>
        ) : filtered.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            {quotes.length === 0 ? 'No quotes yet. Create your first quote!' : 'No quotes match this filter.'}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.5 }}>
              <Checkbox size="small" sx={{ p: 0 }}
                checked={filtered.length > 0 && selected.size === filtered.length}
                indeterminate={selected.size > 0 && selected.size < filtered.length}
                onChange={toggleSelectAll} />
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 100 }}>QUOTE #</Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: 1 }}>TITLE</Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 110 }}>CLIENT</Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 110, textAlign: 'center' }}>STATUS</Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 90, textAlign: 'right' }}>TOTAL</Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 90, textAlign: 'right' }}>BY</Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ width: 80, textAlign: 'right' }}>DATE</Typography>
            </Box>
            {filtered.map((q) => {
              const sc = STATUS_COLORS[q.Status] || '#999';
              const isUrgent = q.Status === 'Internal Review';
              const isHighlighted = q.QuoteKey === highlightKey;
              return (
                <Box key={q.QuoteKey}
                  onClick={() => navigate(ROUTES.QUOTE_EDITOR.replace(':quoteKey', q.QuoteKey))}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75,
                    borderRadius: '8px', cursor: 'pointer',
                    borderLeft: `3px solid ${sc}`,
                    bgcolor: isHighlighted ? alpha(theme.palette.success.main, 0.12) : isUrgent ? alpha(sc, 0.04) : 'background.paper',
                    transition: 'background-color 1.5s ease',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                  }}>
                  <Checkbox size="small" sx={{ p: 0 }} checked={selected.has(q.QuoteKey)}
                    onClick={(e) => toggleSelect(q.QuoteKey, e)} />
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ width: 100, fontSize: '0.8rem' }}>
                    {q.QuoteNumber}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: '0.8rem', minWidth: 0 }}>
                    {q.Title || '(Untitled)'}
                  </Typography>
                  <Typography variant="body2" noWrap color="text.secondary" sx={{ width: 110, fontSize: '0.78rem' }}>
                    {q.ClientName || '—'}
                  </Typography>
                  <Box sx={{ width: 110, textAlign: 'center' }}>
                    <Chip label={q.Status} size="small"
                      sx={{ bgcolor: alpha(sc, 0.12), color: sc, fontWeight: 600, fontSize: '0.68rem', height: 20 }} />
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ width: 90, textAlign: 'right', fontSize: '0.8rem' }}>
                    {fmt(q.GrandTotal, q.Currency)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ width: 90, textAlign: 'right' }}>
                    {q.PreparedByName || ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 80, textAlign: 'right' }}>
                    {q.IssueDate ? new Date(q.IssueDate).toLocaleDateString('en-CA') : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
      <QuickAddInternalQuoteDialog
        open={internalDlgOpen}
        onClose={() => setInternalDlgOpen(false)}
        onCreated={handleInternalCreated}
      />
    </Box>
  );
};

export default QuotesPage;
